import React from "react"
import { FormattedMessage } from "react-intl"
import { SelectOption } from "../../common/types"
import { Intl } from "../../state/helpers/IntlProps"
import { PROPOSAL_TYPE } from "../../state/proposal/ProposalType"
import { CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS, CUSTOM_FIELD_AVAILABLE_FROM } from "./constants"
import { CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES, CUSTOM_FIELD_AVAILABLE_FROM_MESSAGES } from "./messages"
import { customFieldAvailableFrom } from "./types"

export const getCustomFieldListingHeaders = () => {
    return [
        <FormattedMessage
            key="name"
            id="listing.custom_field_definition.list.headers.name_label"
            defaultMessage="Name"
        />,
        <FormattedMessage
            key="available_from"
            id="custom_field_definition.form.edit.available_from_label"
            defaultMessage="Available from"
        />,
        <FormattedMessage
            key="available_to"
            id="listing.custom_field_definition.list.headers.type_label"
            defaultMessage="Type"
        />,
        <FormattedMessage
            key="created_at"
            id="listing.custom_field_definition.list.headers.created_at_label"
            defaultMessage="Created at"
        />,
    ]
}

export function getCustomFieldAvailableFromOptionsByProposalType(proposalType: PROPOSAL_TYPE): customFieldAvailableFrom[] {
    switch (proposalType) {
    case PROPOSAL_TYPE.QUOTE:
        return [CUSTOM_FIELD_AVAILABLE_FROM.QUOTE]
    case PROPOSAL_TYPE.ORDER:
        return [CUSTOM_FIELD_AVAILABLE_FROM.ORDER]
    case PROPOSAL_TYPE.INVOICE:
        return [CUSTOM_FIELD_AVAILABLE_FROM.INVOICE]
    case PROPOSAL_TYPE.ACCOUNT_STATEMENT:
    case PROPOSAL_TYPE.PACKING_SLIP:
    case PROPOSAL_TYPE.PRICE_LIST:
    case PROPOSAL_TYPE.CONTRACT:
    case PROPOSAL_TYPE.SCHEDULING_PAGE:
    case PROPOSAL_TYPE.CREDIT_MEMO_RECEIPT:
    default:
        return []
    }
}

export const getCustomFieldAvailableFromDefaultSelectOptions = (intl: Intl): Array<SelectOption<string>> => {
    return [
        {
            value: CUSTOM_FIELD_AVAILABLE_FROM.INVOICE,
            label: intl.formatMessage(CUSTOM_FIELD_AVAILABLE_FROM_MESSAGES[CUSTOM_FIELD_AVAILABLE_FROM.INVOICE])
        },
        {
            value: CUSTOM_FIELD_AVAILABLE_FROM.RETURN,
            label: intl.formatMessage(CUSTOM_FIELD_AVAILABLE_FROM_MESSAGES[CUSTOM_FIELD_AVAILABLE_FROM.RETURN])
        },
        {
            value: CUSTOM_FIELD_AVAILABLE_FROM.QUOTE,
            label: intl.formatMessage(CUSTOM_FIELD_AVAILABLE_FROM_MESSAGES[CUSTOM_FIELD_AVAILABLE_FROM.QUOTE])
        },
        {
            value: CUSTOM_FIELD_AVAILABLE_FROM.ORDER,
            label: intl.formatMessage(CUSTOM_FIELD_AVAILABLE_FROM_MESSAGES[CUSTOM_FIELD_AVAILABLE_FROM.ORDER])
        },
        {
            value: CUSTOM_FIELD_AVAILABLE_FROM.ORGANIZATIONS,
            label: intl.formatMessage(CUSTOM_FIELD_AVAILABLE_FROM_MESSAGES[CUSTOM_FIELD_AVAILABLE_FROM.ORGANIZATIONS])
        },
        {
            value: CUSTOM_FIELD_AVAILABLE_FROM.PERSON,
            label: intl.formatMessage(CUSTOM_FIELD_AVAILABLE_FROM_MESSAGES[CUSTOM_FIELD_AVAILABLE_FROM.PERSON])
        },
        {
            value: CUSTOM_FIELD_AVAILABLE_FROM.SUPPLIER,
            label: intl.formatMessage(CUSTOM_FIELD_AVAILABLE_FROM_MESSAGES[CUSTOM_FIELD_AVAILABLE_FROM.SUPPLIER])
        },
    ]
}

export const getCustomFieldAcceptedFileExtensionSelectOptions = (intl: Intl): Array<SelectOption<string>> => {
    return [
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.JPG,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.JPG])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PNG,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PNG])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.GIF,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.GIF])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.TIFF,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.TIFF])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.WEBP,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.WEBP])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.SVG,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.SVG])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PDF,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PDF])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.AI,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.AI])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PS,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PS])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.EPS,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.EPS])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PSD,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PSD])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.DOC,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.DOC])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.DOCX,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.DOCX])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.XLS,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.XLS])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.XLSX,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.XLSX])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PP,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PP])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PPX,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.PPX])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.RTF,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.RTF])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.TXT,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.TXT])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.CSV,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.CSV])
        },
        {
            value: CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.VSD,
            label: intl.formatMessage(CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS_MESSAGES[CUSTOM_FIELD_ACCEPTED_FILE_EXTENSIONS.VSD])
        },
    ]
}


// WEBPACK FOOTER //
// ./src/utils/customField/utils.tsx